import i18next from 'i18next';
import type {InitOptions, TFunction} from 'i18next';
import i18nextHttpApi from 'i18next-http-backend';

import {getWhitelabelName} from 'web-app/shared/white-labeling-helpers';

type Setup = (translationsLoadPath: string) => Promise<TFunction>;

const DEFAULT_NAMESPACE = 'translation';

const setup: Setup = async translationsLoadPath => {
    const options = {
        compatibilityJSON: 'v4',
        ns: [DEFAULT_NAMESPACE],
        defaultNS: DEFAULT_NAMESPACE,
        // Only load the default namespace eagerly — other namespaces are loaded on demand via i18next.loadNamespaces
        partialBundledLanguages: true,
        interpolation: {
            escapeValue: false,
            // In v21 "skipOnVariables" defaults to true
            // As we are using nested translations keys
            // we are changing it to false
            // https://www.i18next.com/misc/migration-guide#skiponvariables-true
            skipOnVariables: false,
            defaultVariables: {
                whitelabelName: getWhitelabelName(),
            },
        },
        fallbackLng: {
            'de-CH': ['de-DE', 'en-JM'],
            'no-NO': ['da-DK', 'en-JM'],
            'es-US': ['es-ES', 'en-JM'],
            default: ['en-JM'],
        },
        load: 'currentOnly',
        supportedLngs: ['da-DK', 'en-US', 'en-GB', 'de-DE', 'eo-UY', 'en-JM', 'no-NO', 'es-ES'],
        backend: {
            crossDomain: false,
            withCredentials: false,
            loadPath: (_lngs: string[], namespaces: string[]) => {
                const ns = namespaces[0];
                if (namespaces.length > 1) {
                    console.warn(
                        'Loading multiple namespaces at once is not supported! Found: ',
                        namespaces.join(', '),
                    );
                }
                if (ns === DEFAULT_NAMESPACE) {
                    // Default namespace: locales/{{lng}}-<version>.json (existing behavior)
                    return translationsLoadPath;
                }
                // Namespaced translations: locales/<namespace>/{{lng}}-<version>.json
                return translationsLoadPath.replace('locales/', `locales/${ns}/`);
            },
        },
    } satisfies InitOptions;

    return await i18next.use(i18nextHttpApi).init(options);
};

export default setup;
